import styled from "@emotion/styled";
import { FC, PropsWithChildren, ReactNode } from "react";
import { useSelector } from "react-redux";

import { Info } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import IframeLink from "components/external-link/IframeLink";
import { IDetailedSourceLink, getTicketDetailedSourceLink, ticketDetailedSourcesLabel } from "helpers/tickets";
import { DetailedSource, ITicketSource } from "interfaces/entities/ticket";
import { EmailSettingsRoutes } from "routes/settings/inboxes/routes";
import { selectContactForms } from "store/entities/contact-forms/selectors";
import { selectDefaultMailboxDescription } from "store/entities/license";
import { selectMailboxes } from "store/entities/mailboxes/selectors";
import { withVar } from "themes/helpers";

interface IProps {
  source: ITicketSource | null;
}

export const TicketSourceLabel: FC<IProps> = ({ source }) => {
  const defaultMailboxDescription = useSelector(selectDefaultMailboxDescription);
  const contactForms = useSelector(selectContactForms);
  const mailboxes = useSelector(selectMailboxes);

  if (!source) return "Unknown";

  const { detailedSource, mailboxId, contactFormId, isBooster } = source;

  const link = getTicketDetailedSourceLink(source, contactForms);

  if (detailedSource === DetailedSource.Email) {
    const isDefaultMailbox = !mailboxId;
    const mailboxExists = isDefaultMailbox || Boolean(mailboxes[mailboxId]);
    const description = isDefaultMailbox ? defaultMailboxDescription : mailboxes[mailboxId]?.description || "";
    const hasDescription = description.trim().length > 0;

    if (mailboxExists) {
      return (
        <InfoTooltip label={<Label detailedSource={detailedSource} link={link} />}>
          {hasDescription ? (
            <>
              <b>HelpDesk&lsquo;s forwarding address description:</b>
              <br />
              {description}
            </>
          ) : (
            <>
              Add description to HelpDesk&lsquo;s forwarding address to know from which addresses the message is
              forwarded.
              <br />
              <IframeLink
                to={
                  isDefaultMailbox
                    ? EmailSettingsRoutes.ForwardingAddressesWithSetupDefault
                    : `${EmailSettingsRoutes.ForwardingAddressesWithSetupModal}${mailboxes[mailboxId].id}`
                }
              >
                <Button kind="link-inverted">Settings</Button>
              </IframeLink>
            </>
          )}
        </InfoTooltip>
      );
    }
  }

  if (detailedSource === DetailedSource.ContactForm || detailedSource === DetailedSource.WordPress) {
    const isLiveChatWelcomeScreenContactForm = Boolean(isBooster);

    if (contactFormId) {
      const contactForm = contactForms[contactFormId];
      const contactFormExists = Boolean(contactForm);

      if (contactFormExists) {
        return (
          <StyledInfoTooltip label={<Label detailedSource={detailedSource} link={link} />}>
            <>
              <b>Contact form name:</b>
              <br />
              {contactForm.name}
            </>
          </StyledInfoTooltip>
        );
      }
    } else if (isLiveChatWelcomeScreenContactForm) {
      return (
        <StyledInfoTooltip label={<Label detailedSource={detailedSource} link={link} />}>
          LiveChat welcome screen form
        </StyledInfoTooltip>
      );
    }
  }

  return <Label detailedSource={detailedSource} link={link} />;
};

interface ILabelProps {
  detailedSource: DetailedSource;
  link: IDetailedSourceLink | null;
}

const Label: FC<ILabelProps> = ({ detailedSource, link }) => {
  const label = ticketDetailedSourcesLabel[detailedSource];

  if (!link) {
    return label;
  }

  if (link.external) {
    return (
      <Button kind="link-light" href={link.href} target="_blank" rel="noopener noreferrer">
        {label}
      </Button>
    );
  }

  return <IframeLink to={link.href}>{label}</IframeLink>;
};

const InfoTooltip: FC<PropsWithChildren<{ label: ReactNode; className?: string }>> = ({
  label,
  className,
  children,
}) => {
  return (
    <TooltipWrapper>
      {label}
      <Tooltip
        className={className}
        kind="invert"
        placement="left"
        triggerRenderer={<Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />}
      >
        {children}
      </Tooltip>
    </TooltipWrapper>
  );
};

const TooltipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
`;

const StyledInfoTooltip = styled(InfoTooltip)`
  min-width: 200px;
  max-width: 340px;
`;
